import { Component, EventEmitter, OnDestroy, OnInit, SecurityContext, signal, inject, WritableSignal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PagedResult, PagingOptions } from 'src/app/shared/models/paging';
import { Invitation, InvitationSearchEvent } from './charging-user-invitations-modal.model';
import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, startWith, take, tap } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { UserInvitationService } from 'src/app/shared/services/user.invitation.service';
import { MatTableDataSource } from '@angular/material/table';
import { AppState } from 'src/app/app.state';
import { Sort } from '@angular/material/sort';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InvitationStatus } from 'src/app/shared/enums/invitation-status.enum';
import { DomSanitizer } from '@angular/platform-browser';
import { Organization } from '../../organization/organization-models';
import { CommonModule, AsyncPipe } from '@angular/common';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { EnumToArrayPipe } from '../../../shared/pipes/enum-to-array.pipe';
import { TranslateInvitationStatusEnum } from '../../../shared/pipes/translate-invitation-status-enum.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule } from '@angular/material/paginator';

@Component({
  selector: 'adm-charging-user-invitations-modal',
  templateUrl: './charging-user-invitations-modal.component.html',
  styleUrls: ['./charging-user-invitations-modal.component.scss'],
  imports: [
    CommonModule,
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatPaginatorModule,
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    LoadingSpinnerComponent,
    EnumToArrayPipe,
    TranslateInvitationStatusEnum,
    CustomDropDownArrowDirective,
    AdmOrgTypeaheadComponent,
  ],
})
export class CharingUserInvitationsModalComponent extends PagerBase implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<CharingUserInvitationsModalComponent>>(MatDialogRef);
  private invitationService = inject(UserInvitationService);
  appState = inject(AppState);
  private sanitizer = inject(DomSanitizer);
  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });

  public invitations: Observable<PagedResult<Invitation>>;
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public pageEvent = new EventEmitter<PageEvent>();
  public paginator: MatPaginator;
  private initialInvitationSearchEvent: InvitationSearchEvent = {
    paging: {
      length: 0,
      pageIndex: 0,
      pageSize: 0,
      previousPageIndex: 0,
    },
    sorting: {
      active: 'created',
      direction: 'desc',
    },
    organizationId: undefined,
    query: '',
    status: undefined,
  };
  public invitationSearchEvent$: BehaviorSubject<InvitationSearchEvent> = new BehaviorSubject<InvitationSearchEvent>(
    this.initialInvitationSearchEvent
  );

  public pagingOptions: PagingOptions = {
    pageSize: this.defaultPageSize,
    pageSizeOptions: this.pageSizes,
  };
  public displayColumns = ['emailAddress', 'organization', 'invitationStatus', 'updatedBy', 'updated'];
  public statuses = InvitationStatus;

  data$: BehaviorSubject<Invitation[]>;
  dataSource: MatTableDataSource<Invitation> = new MatTableDataSource<Invitation>();

  public invitationCount: number;
  public pageIndex: number;
  filterModel: FormGroup = new FormGroup({
    query: new FormControl(''),
    selectedOrganizationId: new FormControl<number | null>(null),
    selectedStatus: new FormControl<InvitationStatus | null>(null),
    sorting: new FormControl<Sort | null>({ active: 'created', direction: 'desc' }),
    paging: new FormControl<PageEvent | null>(null),
  });

  async ngOnInit() {
    this.dataSource.paginator = this.paginator;

    this.filterModel.valueChanges
      .pipe(
        startWith(''),
        debounceTime(700),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(val => {
        if (!val) {
          val = this.filterModel.value;
        }
        let query: string | undefined = undefined;
        let selectedStatus: InvitationStatus | undefined = undefined;
        if (val) {
          query = val.query ?? undefined;
          selectedStatus = val.selectedStatus ?? undefined;
        }

        this.invitationSearchEvent$.pipe(take(1)).subscribe(currentEvent => {
          const newEvent = {
            ...currentEvent,
          };
          newEvent.query = query;
          newEvent.status = selectedStatus;
          newEvent.organizationId = val.selectedOrganizationId ?? undefined;

          if (currentEvent.paging) {
            newEvent.paging.pageIndex = 0;
          }

          if (val.sorting) {
            newEvent.sorting = val.sorting;
          }
          if (val.paging) {
            newEvent.paging = val.paging;
          }
          if (JSON.stringify(currentEvent) !== JSON.stringify(newEvent)) {
            this.invitationSearchEvent$.next(newEvent);
          }
        });
      });

    this.invitationSearchEvent$.subscribe(val => {
      this.getInvitations(val);
    });
  }

  get selectedOrganizationId() {
    return this.filterModel.controls['selectedOrganizationId'];
  }

  private getInvitations(invitationSearchEvent: InvitationSearchEvent) {
    this.loading$.next(true);
    this.invitationService.getInvitations(invitationSearchEvent).subscribe(result => {
      this.dataSource.data = result.items;
      this.invitationCount = result.totalCount;
      this.pageIndex = result.pageIndex;
      this.data$ = this.dataSource.connect();
      this.loading$.next(false);
    });
  }
  public pageSwitch(event: PageEvent) {
    this.invitationSearchEvent$.pipe(take(1)).subscribe(currentEvent => {
      const newEvent = {
        ...currentEvent,
        paging: event,
      };
      this.invitationSearchEvent$.next(newEvent);
    });
  }

  public sortData(event: Sort) {
    this.invitationSearchEvent$.pipe(take(1)).subscribe(currentEvent => {
      if (currentEvent.paging) {
        currentEvent.paging.pageIndex = 0;
      }
      if (event.direction === '') {
        event.direction = 'desc';
      }
      currentEvent.sorting = event;
      this.invitationSearchEvent$.next(currentEvent);
    });
  }

  public resetFilter() {
    this.filterModel.patchValue({
      query: '',
      selectedOrganizationId: null,
      selectedStatus: null,
    });
    this.resetOrgTrigger.update(current => ({ reset: !current.reset }));
  }
  organizationSelected(organization: Organization | null) {
    this.filterModel.patchValue({
      selectedOrganizationId: organization ? organization.id : null,
    });
  }

  ngOnDestroy(): void {
    if (this.dataSource) {
      this.dataSource.disconnect();
    }
  }
}
